/**
 * ForwardMerchantsAdmin.tsx
 *
 * Admin › Payments › Forward › Merchants — the practice's card-processing
 * merchant accounts (`aa_accounts_forward`). Ported from the legacy admin page,
 * which was the only place these could be managed.
 *
 * ── Why there are no API key fields ────────────────────────────────────────
 * The Forward credentials are system-level: one pair, shared by every merchant
 * on the account, set outside this software. They are not editable here, the
 * API never returns them, and a new merchant inherits them server-side.
 *
 * The list is the Admin list standard (Lead Sources; user, 2026-10-14): a
 * search, a "Show inactive" switch (it re-asks the server, as legacy's filter
 * should have), and on each row the Edit / Turn off icons — Turn back on on a
 * merchant that is off — with a click on a merchant in use opening it.
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): the list shows every merchant in
 * full, and search and "Show inactive" still work, but there is no Add
 * Merchant, Transfer Memberships, row icon or row click. Nothing needs opening
 * to read: a row shows everything the merchant dialog holds.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRightLeft, Plus } from 'lucide-react';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { Badge } from '@/components/ui/Badge';
import { FormField } from '@/components/ui/FormField';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import * as adminSvc from '@/services/admin.service';
import type { Facility, ForwardMerchant } from '@/services/admin.service';

const NO_FACILITY = 0;

/** The API's plain-language failure text, or a usable fallback. */
function errorText(err: unknown, fallback: string): string {
  const message = (err as { response?: { data?: { error?: { message?: unknown } } } })
    ?.response?.data?.error?.message;
  return typeof message === 'string' && message.trim() !== '' ? message : fallback;
}

// ─── Form ─────────────────────────────────────────────────────────────────

interface MerchantForm {
  friendlyTitle: string;
  accountId: string;
  facilityID: number;
  isDefault: boolean;
}

const EMPTY_FORM: MerchantForm = {
  friendlyTitle: '',
  accountId: '',
  facilityID: NO_FACILITY,
  isDefault: false,
};

interface MerchantModalProps {
  merchant?: ForwardMerchant | null;
  facilities: Facility[];
  onClose: () => void;
  onSaved: () => void;
}

function ForwardMerchantModal({ merchant, facilities, onClose, onSaved }: MerchantModalProps) {
  const isEdit = merchant != null;

  const [form, setForm] = useState<MerchantForm>(() =>
    merchant
      ? {
          friendlyTitle: merchant.friendlyTitle,
          accountId: merchant.account_id,
          facilityID: merchant.facilityID ?? NO_FACILITY,
          isDefault: merchant.isDefault,
        }
      : EMPTY_FORM,
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const facilityOptions = useMemo(
    () => [
      { value: NO_FACILITY, label: 'All facilities' },
      ...facilities.map((f) => ({ value: f.facilityId, label: f.facilityName })),
    ],
    [facilities],
  );

  function set<K extends keyof MerchantForm>(field: K, value: MerchantForm[K]) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  const canSubmit =
    form.friendlyTitle.trim() !== '' && (isEdit || form.accountId.trim() !== '');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    setSaving(true);
    setError(null);
    try {
      if (merchant) {
        // account_id is left out on purpose — it is fixed once the merchant
        // exists, and the API refuses a request that carries it.
        await adminSvc.updateForwardMerchant(merchant.accountForwardID, {
          friendlyTitle: form.friendlyTitle.trim(),
          facilityID: form.facilityID > 0 ? form.facilityID : null,
          isDefault: form.isDefault,
        });
      } else {
        await adminSvc.createForwardMerchant({
          friendlyTitle: form.friendlyTitle.trim(),
          account_id: form.accountId.trim(),
          facilityID: form.facilityID > 0 ? form.facilityID : null,
          isDefault: form.isDefault,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(
        errorText(
          err,
          'This merchant could not be saved. Try again in a moment, or contact Prospera support if it keeps happening.',
        ),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit Merchant' : 'Add Merchant'}
      size="md"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !canSubmit} loading={saving}>
            {isEdit ? 'Save Changes' : 'Add Merchant'}
          </Button>
        </>
      )}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
            {error}
          </p>
        )}

        <FormField label="Name" required htmlFor="forward-merchant-name">
          <Input
            id="forward-merchant-name"
            value={form.friendlyTitle}
            onChange={(e) => set('friendlyTitle', e.target.value)}
            placeholder="The name staff will see on payments and payouts"
            required
            autoFocus
          />
        </FormField>

        <FormField
          label="Forward account ID"
          required={!isEdit}
          htmlFor="forward-merchant-account-id"
          helpText={isEdit ? 'This cannot be changed once a merchant is in use.' : undefined}
        >
          <Input
            id="forward-merchant-account-id"
            value={form.accountId}
            onChange={(e) => set('accountId', e.target.value)}
            disabled={isEdit}
            required={!isEdit}
          />
        </FormField>

        <FormField label="Facility">
          <Select
            options={facilityOptions}
            value={form.facilityID}
            onChange={(v) => set('facilityID', v ?? NO_FACILITY)}
          />
        </FormField>

        <FormField label="">
          <Checkbox
            checked={form.isDefault}
            onChange={(e) => set('isDefault', e.target.checked)}
            label="Use this merchant by default"
          />
        </FormField>
      </form>
    </Modal>
  );
}

// ─── Transfer memberships ─────────────────────────────────────────────────

interface TransferModalProps {
  /** The practice's merchants that are turned on — the only ones legacy offered. */
  merchants: ForwardMerchant[];
  onClose: () => void;
  onTransferred: (message: string) => void;
}

/**
 * Legacy's "Transfer Memberships Between Merchants": when a practice changes
 * merchant, every active membership on the old one has to move to the new
 * one, or its next charge declines. Nothing records which memberships moved,
 * so it cannot be undone — hence the confirm.
 */
function TransferMembershipsModal({ merchants, onClose, onTransferred }: TransferModalProps) {
  const [fromId, setFromId] = useState<number | null>(null);
  const [toId, setToId] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = merchants.map((m) => ({ value: m.accountForwardID, label: m.friendlyTitle }));
  const nameOf = (id: number | null) => merchants.find((m) => m.accountForwardID === id)?.friendlyTitle ?? '';
  const same = fromId !== null && fromId === toId;
  const canTransfer = fromId !== null && toId !== null && !same;

  async function handleTransfer() {
    if (fromId === null || toId === null) return;
    try {
      const count = await adminSvc.transferForwardMemberships(fromId, toId);
      onTransferred(
        `${count} ${count === 1 ? 'membership' : 'memberships'} moved from "${nameOf(fromId)}" to "${nameOf(toId)}".`,
      );
      onClose();
    } catch (err) {
      setConfirming(false);
      setError(
        errorText(
          err,
          'The memberships could not be transferred. Try again in a moment, or contact Prospera support if it keeps happening.',
        ),
      );
    }
  }

  return (
    <>
      <Modal
        isOpen={!confirming}
        onClose={onClose}
        title="Transfer Memberships"
        size="md"
        footer={(
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => setConfirming(true)} disabled={!canTransfer}>
              Transfer
            </Button>
          </>
        )}
      >
        <div className="space-y-4">
          {error && (
            <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </p>
          )}

          <p className="text-sm text-slate-600">
            Every active membership charged through the first merchant will be charged through the
            second from now on. Use this when the practice changes merchant: memberships left on the
            old one decline.
          </p>

          <Select
            label="From merchant"
            placeholder="Select source merchant…"
            options={options}
            value={fromId}
            onChange={setFromId}
          />

          <Select
            label="To merchant"
            placeholder="Select destination merchant…"
            options={options}
            value={toId}
            onChange={setToId}
            error={same ? 'Choose two different merchants.' : undefined}
          />
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirming}
        title="Transfer memberships?"
        message={`Every active membership on "${nameOf(fromId)}" will move to "${nameOf(toId)}". This cannot be undone.`}
        confirmLabel="Transfer"
        variant="danger"
        onConfirm={handleTransfer}
        onClose={() => setConfirming(false)}
      />
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────

const COLUMNS: AdminListColumn[] = [
  { key: 'name', header: 'Name', sortable: true },
  { key: 'accountId', header: 'Forward account ID', className: 'w-[22%]' },
  { key: 'facility', header: 'Facility', sortable: true, className: 'w-[22%]' },
  { key: 'default', header: 'Default', className: 'w-24' },
  { key: 'status', header: 'Status', className: 'w-24' },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

const SORT_COMPARE: ListSortCompare<ForwardMerchant> = {
  name: (a, b) => compareText(a.friendlyTitle, b.friendlyTitle),
  facility: (a, b) => compareText(a.facilityName || 'All facilities', b.facilityName || 'All facilities'),
};

export function ForwardMerchantsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [items, setItems] = useState<ForwardMerchant[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState<ForwardMerchant | null>(null);
  const [deactivateItem, setDeactivateItem] = useState<ForwardMerchant | null>(null);
  const [showTransfer, setShowTransfer] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const activeMerchants = useMemo(() => items.filter((m) => m.active), [items]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [merchants, facilityData] = await Promise.all([
        adminSvc.listForwardMerchants(showInactive),
        adminSvc.listFacilities(),
      ]);
      setItems(merchants);
      setFacilities(facilityData);
      setError(null);
    } catch (err) {
      setError(
        errorText(
          err,
          'The merchant list could not be loaded. Try again in a moment, or contact Prospera support if it keeps happening.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDeactivate() {
    if (!deactivateItem) return;
    try {
      await adminSvc.deactivateForwardMerchant(deactivateItem.accountForwardID);
      setDeactivateItem(null);
      setError(null);
      void load();
    } catch (err) {
      setDeactivateItem(null);
      setError(
        errorText(
          err,
          'This merchant could not be switched off. Try again in a moment, or contact Prospera support if it keeps happening.',
        ),
      );
    }
  }

  async function handleReactivate(row: ForwardMerchant) {
    try {
      await adminSvc.restoreForwardMerchant(row.accountForwardID);
      setError(null);
      void load();
    } catch (err) {
      setError(
        errorText(
          err,
          'This merchant could not be switched back on. Try again in a moment, or contact Prospera support if it keeps happening.',
        ),
      );
    }
  }

  const query = search.trim().toLowerCase();
  const rows = useMemo(
    () =>
      query === ''
        ? items
        : items.filter((m) => m.friendlyTitle.toLowerCase().includes(query) || m.account_id.toLowerCase().includes(query)),
    [items, query],
  );
  const sorting = useListSort(rows, { initialKey: 'name', compare: SORT_COMPARE });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showInactive] });

  return (
    <AdminListPage
      title="Forward Merchants"
      subtitle="The practice's card-processing merchant accounts."
      actions={
        <>
          <Button
            variant="secondary"
            leftIcon={<ArrowRightLeft className="h-4 w-4" />}
            onClick={() => {
              setNotice(null);
              setShowTransfer(true);
            }}
            disabled={activeMerchants.length < 2}
          >
            Transfer Memberships
          </Button>
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowCreate(true)}>
            Add Merchant
          </Button>
        </>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name or account ID…"
          searchLabel="Search merchants"
          showDeleted={showInactive}
          onShowDeletedChange={setShowInactive}
          showDeletedId="forward-merchants-show-inactive"
          showDeletedLabel="Show inactive"
        />
      }
    >
      {error && (
        <p className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
          {error}
        </p>
      )}

      {notice && (
        <p role="status" className="mb-4 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          {notice}
        </p>
      )}

      {loading ? (
        <SpinnerOverlay label="Loading merchants…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No merchants found' : 'No merchants yet'}
          hint={query ? 'Try adjusting your search.' : undefined}
        />
      ) : (
        <>
          <AdminListTable
            columns={locked ? LOCKED_COLUMNS : COLUMNS}
            aria-label="Forward merchants"
            sort={sorting.sort}
            onSortChange={sorting.onSortChange}
          >
            {pagination.pageItems.map((row) => {
              const off = !row.active;
              // A merchant that is in use opens to edit; one that is off is turned back on first.
              const opens = !locked && !off;
              return (
                <tr
                  key={row.accountForwardID}
                  className={adminRowClass({ clickable: opens, deleted: off })}
                  onClick={opens ? () => setEditItem(row) : undefined}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <span
                      data-testid="merchant-name"
                      className={cn('font-medium', off ? 'text-slate-500' : 'text-slate-900')}
                    >
                      {row.friendlyTitle}
                    </span>
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    <span className="font-mono text-sm text-slate-700">{row.account_id || '—'}</span>
                  </td>
                  <td className={cn(ADMIN_TD_CLASS, 'text-slate-700')}>{row.facilityName || 'All facilities'}</td>
                  <td className={ADMIN_TD_CLASS}>
                    <Badge variant={row.isDefault ? 'info' : 'neutral'}>{row.isDefault ? 'Yes' : 'No'}</Badge>
                  </td>
                  <td className={ADMIN_TD_CLASS}>
                    <Badge variant={row.active ? 'success' : 'neutral'}>{row.active ? 'In use' : 'Off'}</Badge>
                  </td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {off ? (
                          <AdminRowAction
                            kind="restore"
                            title="Turn back on"
                            label={`Turn back on ${row.friendlyTitle}`}
                            onClick={() => void handleReactivate(row)}
                          />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${row.friendlyTitle}`} onClick={() => setEditItem(row)} />
                            <AdminRowAction
                              kind="delete"
                              title="Turn off"
                              label={`Turn off ${row.friendlyTitle}`}
                              onClick={() => setDeactivateItem(row)}
                            />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['merchant', 'merchants']} />
        </>
      )}

      {showCreate && (
        <ForwardMerchantModal facilities={facilities} onClose={() => setShowCreate(false)} onSaved={load} />
      )}

      {editItem && (
        <ForwardMerchantModal
          merchant={editItem}
          facilities={facilities}
          onClose={() => setEditItem(null)}
          onSaved={load}
        />
      )}

      {showTransfer && (
        <TransferMembershipsModal
          merchants={activeMerchants}
          onClose={() => setShowTransfer(false)}
          onTransferred={setNotice}
        />
      )}

      <ConfirmDialog
        isOpen={deactivateItem !== null}
        title="Turn off this merchant?"
        message={
          deactivateItem
            ? `"${deactivateItem.friendlyTitle}" will stop being offered for new payments. Past payments and payouts keep it, and you can turn it back on at any time.`
            : ''
        }
        confirmLabel="Turn off"
        variant="danger"
        onConfirm={handleDeactivate}
        onClose={() => setDeactivateItem(null)}
      />
    </AdminListPage>
  );
}

export default ForwardMerchantsAdmin;
